import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import and from 'editor/helpers/and';
import eq from 'editor/helpers/eq';
import gte from 'editor/helpers/gte';
import htmlId from 'editor/helpers/html-id';
import pick from 'editor/helpers/pick';
import queue from 'editor/helpers/queue';
import { CompositeCommand } from 'editor/models/command';
import type Drumset from 'editor/models/drumset';
import { Instrument, type IInstrument } from 'editor/models/drumset';
import {
  AddInstrumentToDrumsetCommand,
  AddNewInstrumentToDrumsetCommand,
  DeleteInstrumentCommand,
  RenameInstrumentCommand,
  UpdateDrumsetVolumeCommand,
  UpdateInstrumentChokeGroupCommand,
  UpdateInstrumentFillChokeDelayCommand,
  UpdateInstrumentFillChokeGroupCommand,
  UpdateInstrumentIdCommand,
  UpdateInstrumentNonPercussionCommand,
  UpdateInstrumentPolyphonyCommand,
  UpdateInstrumentVolumeCommand,
} from 'editor/models/drumset-commands';
import onClickOutside from 'editor/modifiers/on-click-outside';
import onKey from 'editor/modifiers/on-key';
import selected from 'editor/modifiers/selected';
import type ProjectManagerService from 'editor/services/project-manager';
import type SongPlayerService from 'editor/services/song-player';
import type UndoManagerService from 'editor/services/undo-manager';
import gmDrumMap from 'editor/utils/gm-drum-map';
import nameFilter from 'editor/utils/name-filter';
import velcro from 'ember-velcro/modifiers/velcro';
import Button from './button';
import EditableField from './editable-field';
import InstrumentImpport from './instrument-import';
import InstrumentSettingsEditor from './instrument-settings-editor';
import MenuButton from './menu-button';
import OptionButton from './option-button';
import SampleEditor from './sample-editor';
import SelectableItem from './selectable-item';

const UNDO_DELAY = 800;

interface DrumsetEditorSignature {
  Element: HTMLDivElement;
  Args: {
    drumset: Drumset;
    // eslint-disable-next-line no-unused-vars
    renameDrumset: (name: string) => void;
  };
  Blocks: {
    // default: [];
  };
}

export class MultiInstrument implements IInstrument {
  instruments: IInstrument[];
  id = -1;
  name = 'Multiple Instruments';

  constructor(instruments: IInstrument[]) {
    this.instruments = instruments;
  }

  markDirty() {
    this.instruments.forEach((i) => i.markDirty());
  }

  // if all instruments share the same chokeGroup, return that value, else return -1
  get chokeGroup() {
    const chokeGroups = this.instruments.map((i) => i.chokeGroup);
    return chokeGroups.every((cg) => cg === chokeGroups[0]) ? chokeGroups[0]! : -1;
  }

  set chokeGroup(value: number) {
    this.instruments.forEach((i) => (i.chokeGroup = value));
  }

  get poly() {
    const polys = this.instruments.map((i) => i.poly);
    return polys.every((p) => p === polys[0]) ? polys[0]! : -1;
  }

  set poly(value: number) {
    this.instruments.forEach((i) => (i.poly = value));
  }

  get volume() {
    const volumes = this.instruments.map((i) => i.volume);
    return volumes.every((v) => v === volumes[0]) ? volumes[0]! : -1;
  }

  get volumeDb() {
    if (this.volume === -1) return -1;
    return Number((20 * Math.log10(this.volume / 100)).toFixed(1));
  }

  set volume(value: number) {
    this.instruments.forEach((i) => (i.volume = value));
  }

  get fillChokeGroup() {
    const fillChokeGroups = this.instruments.map((i) => i.fillChokeGroup);
    return fillChokeGroups.every((fcg) => fcg === fillChokeGroups[0])
      ? fillChokeGroups[0]!
      : -1;
  }

  set fillChokeGroup(value: number) {
    this.instruments.forEach((i) => (i.fillChokeGroup = value));
  }

  get fillChokeDelay() {
    const fillChokeDelays = this.instruments.map((i) => i.fillChokeDelay);
    return fillChokeDelays.every((fcd) => fcd === fillChokeDelays[0])
      ? fillChokeDelays[0]!
      : -1;
  }

  set fillChokeDelay(value: number) {
    this.instruments.forEach((i) => (i.fillChokeDelay = value));
  }

  get nonPercussion() {
    const nonPercussions = this.instruments.map((i) => i.nonPercussion);
    return nonPercussions.every((np) => np === nonPercussions[0])
      ? nonPercussions[0]! == 1
        ? 1
        : 0
      : -1;
  }

  set nonPercussion(value: number) {
    this.instruments.forEach((i) => (i.nonPercussion = value ? 1 : 0));
  }
}

function allInstruments(instrument: IInstrument): IInstrument[] {
  if (instrument instanceof MultiInstrument) {
    return instrument.instruments.map((i) => i);
  } else {
    return [instrument];
  }
}

export default class DrumsetEditorComponent extends Component<DrumsetEditorSignature> {
  @service declare projectManager: ProjectManagerService;
  @service declare songPlayer: SongPlayerService;
  @service declare undoManager: UndoManagerService;
  @service declare router: RouterService;

  @tracked showInstrumentList = false;
  @tracked showImportModal = false;
  @tracked menuInstrument?: IInstrument;
  @tracked selectedInstruments: IInstrument[] = [];
  @tracked ignoreNextClickOutside: boolean = false;

  undoUpdateTimer: number = 0;

  constructor(owner: unknown, args: DrumsetEditorSignature['Args']) {
    super(owner, args);
    this.router.on('routeDidChange', () => {
      if (this.drumset.playableInstruments.length === 0) this.selectedInstruments = [];
      else this.selectedInstruments = [this.drumset.playableInstruments[0]!];
    });
    if (this.drumset.playableInstruments.length === 0) this.selectedInstruments = [];
    else this.selectedInstruments = [this.drumset.playableInstruments[0]!];
  }

  get drumset() {
    return this.args.drumset!;
  }

  get instruments(): IInstrument[] {
    return this.drumset.playableInstruments;
  }

  get availableInstruments() {
    return Array.from(gmDrumMap.values()).filter(
      ({ id }) => !this.instruments.some((i) => i.id === id),
    );
  }

  @action
  isSelectedInstrument(instrument: IInstrument) {
    return this.selectedInstruments.includes(instrument);
  }

  get instrument(): IInstrument {
    if (this.selectedInstruments.length === 1) {
      return this.selectedInstruments[0]!;
    } else {
      return new MultiInstrument(this.selectedInstruments);
    }
  }

  @action
  selectInstrument(instrument: IInstrument | undefined, ev?: KeyboardEvent | MouseEvent) {
    ev?.stopPropagation();
    if (instrument === undefined) {
      this.selectedInstruments = [];
      return;
    }

    if (ev?.ctrlKey || ev?.metaKey) {
      if (this.isSelectedInstrument(instrument)) {
        this.selectedInstruments = this.selectedInstruments.filter(
          (i) => i !== instrument,
        );
      } else {
        this.selectedInstruments = [...this.selectedInstruments, instrument];
      }
    } else if (ev?.shiftKey) {
      const firstSelectedInstrument = this.selectedInstruments.at(0);
      const lastSelectedInstrument = this.selectedInstruments.at(-1);
      if (firstSelectedInstrument && lastSelectedInstrument) {
        const firstIndex = this.instruments.indexOf(firstSelectedInstrument);
        const lastIndex = this.instruments.indexOf(lastSelectedInstrument);
        const currentIndex = this.instruments.indexOf(instrument);
        if (currentIndex < firstIndex) {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.instruments.slice(currentIndex, firstIndex + 1),
          ];
        } else if (currentIndex > lastIndex) {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.instruments.slice(lastIndex, currentIndex + 1),
          ];
        } else {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.instruments.slice(firstIndex, currentIndex + 1),
          ];
        }
      } else {
        this.selectedInstruments = [instrument];
      }
    } else {
      this.selectedInstruments = [instrument];
    }
    // remove window selection
    window.getSelection()?.removeAllRanges();
  }

  @action
  selectNextInstrument(ev: KeyboardEvent) {
    ev.preventDefault();
    const firstSelectedInstrument = this.selectedInstruments.at(0);
    const lastSelectedInstrument = this.selectedInstruments.at(-1);
    if (firstSelectedInstrument && lastSelectedInstrument) {
      const firstIndex = this.instruments.indexOf(firstSelectedInstrument);
      const lastIndex = this.instruments.indexOf(lastSelectedInstrument);
      if (ev.shiftKey && firstIndex <= lastIndex) {
        let nextIndex = lastIndex + 1;
        if (nextIndex >= this.instruments.length) {
          nextIndex = lastIndex;
        }
        this.selectedInstruments = this.instruments.slice(firstIndex, nextIndex + 1);
      } else if (ev.shiftKey && firstIndex >= lastIndex) {
        this.selectedInstruments = this.selectedInstruments.slice(0, -1);
      } else if (!ev.shiftKey) {
        let nextIndex = lastIndex + 1;
        if (nextIndex >= this.instruments.length) {
          nextIndex = 0;
        }
        this.selectedInstruments = this.instruments.slice(nextIndex, nextIndex + 1);
      }
    } else {
      this.selectedInstruments = this.instruments.slice(0, 1);
    }
    document.querySelector('.instrument.selected')?.scrollIntoView({ block: 'start' });
  }

  @action
  selectPreviousInstrument(ev: KeyboardEvent) {
    ev.preventDefault();
    const firstSelectedInstrument = this.selectedInstruments.at(0);
    const lastSelectedInstrument = this.selectedInstruments.at(-1);
    if (firstSelectedInstrument && lastSelectedInstrument) {
      const firstIndex = this.instruments.indexOf(firstSelectedInstrument);
      const lastIndex = this.instruments.indexOf(lastSelectedInstrument);
      if (ev.shiftKey && lastIndex <= firstIndex) {
        let nextIndex = lastIndex - 1;
        if (nextIndex < 0) {
          nextIndex = 0;
        }
        this.selectedInstruments = [
          ...this.selectedInstruments,
          this.instruments.at(nextIndex)!,
        ];
      } else if (ev.shiftKey && lastIndex >= firstIndex) {
        this.selectedInstruments = this.selectedInstruments.slice(0, -1);
      } else if (!ev.shiftKey) {
        let nextIndex = lastIndex - 1;
        if (nextIndex < 0) {
          nextIndex = this.instruments.length - 1;
        }
        this.selectedInstruments = this.instruments.slice(nextIndex, nextIndex + 1);
      }
    } else {
      this.selectedInstruments = this.instruments.slice(-1);
    }
    document.querySelector('.instrument.selected')?.scrollIntoView({ block: 'end' });
  }

  @action
  deleteSelectedInstruments() {
    const commands = this.selectedInstruments.map((i) => {
      return new DeleteInstrumentCommand(this.drumset, <Instrument>i);
    });
    const command = new CompositeCommand(commands[0]!.name, commands);
    command.on('execute', () => {
      this.selectedInstruments = this.instruments.slice(0, 1);
    });
    command.on('reverse', () => {
      this.selectedInstruments = commands.map((c) => c.instrument);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  showMenuFor(instrument: IInstrument | undefined, ev?: MouseEvent) {
    if (!instrument && this.ignoreNextClickOutside) {
      this.ignoreNextClickOutside = false;
      return;
    }
    if (this.menuInstrument != instrument) {
      ev?.preventDefault();
      ev?.stopPropagation();
      if (this.menuInstrument && instrument) {
        this.ignoreNextClickOutside = true;
      }
      this.menuInstrument = instrument;
    }
    if (!instrument) {
      this.menuInstrument = undefined;
      return;
    }
  }

  @action
  shouldShowMenuFor(instrument: IInstrument) {
    return this.menuInstrument === instrument;
  }

  @action
  toggleShowInstrumentList(force: boolean | Event) {
    if (typeof force === 'boolean') {
      this.showInstrumentList = force;
      return;
    }
    this.showInstrumentList = !this.showInstrumentList;
  }

  @action
  toggleShowImportModal(force: boolean | Event) {
    if (typeof force === 'boolean') {
      this.showImportModal = force;
      return;
    }
    this.showImportModal = !this.showImportModal;
  }

  @action
  addInstrument(inst: { id: number; name: string } | Instrument | undefined) {
    const command = new AddNewInstrumentToDrumsetCommand(
      this.drumset,
      inst?.name ?? 'Unknown',
      inst?.id,
    );
    command.on('execute', (instrument) => {
      this.selectInstrument(instrument);
    });
    command.on('reverse', () => {
      this.selectInstrument(undefined);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  addInstruments(instruments: Instrument[]) {
    const filteredOutInstruments = instruments.filter(
      (i) => !this.drumset.isDRMX && i.velocities.length > 16,
    );

    if (filteredOutInstruments.length > 0) {
      alert(
        `The following instruments were not added because they exceed 16 samples:\n\n${filteredOutInstruments
          .map((i) => i.name)
          .join('\n')}`,
      );
    }
    const commands = instruments
      .filter((i) => (this.drumset.isDRMX ? true : i.velocities.length <= 16))
      .map((i) => new AddInstrumentToDrumsetCommand(this.drumset, i.copy()));
    if (commands.length <= 0) {
      return;
    }
    const command = new CompositeCommand(commands[0]!.name, commands);
    command.on('execute', (instruments: Instrument[]) => {
      this.drumset.prepareAudio(this.songPlayer.audioContext);
      this.selectInstrument(instruments.at(0));
    });
    command.on('reverse', () => {
      this.selectInstrument(undefined);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  renameInstrument(instrument: IInstrument, name: string) {
    if (instrument instanceof Instrument) {
      this.undoManager.executeCommand(new RenameInstrumentCommand(instrument, name));
    }
  }

  @action
  deleteInstrument(instrument: IInstrument) {
    const command = new DeleteInstrumentCommand(this.drumset, <Instrument>instrument);
    command.on('execute', () => {
      this.selectInstrument(undefined);
    });
    command.on('reverse', () => {
      this.selectInstrument(instrument);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  popupMiddleware() {
    return [
      // Calculates the maximum height for the accent popup list
      {
        name: 'Max height',
        options: {},
        async fn(state: any) {
          const availableHeight = document.documentElement.clientHeight;
          const top = state.rects.reference.y + state.rects.reference.height;
          const height = availableHeight - top - 20;
          state.elements.floating.style.maxHeight = `${height}px`;
          return state;
        },
      },
    ];
  }

  get availableMidiIds() {
    return this.drumset.availableInstruments
      .map((i) => i.id)
      .map((id) => {
        const gmInstrument = gmDrumMap.get(id);
        if (gmInstrument) {
          return { id: gmInstrument.id, name: gmInstrument.name };
        } else {
          return { id, name: '' };
        }
      });
  }

  updateDrumsetVolumeFrom?: number = undefined;
  @action
  updateDrumsetVolume(value: string) {
    const volume = parseInt(value);
    this.updateDrumsetVolumeFrom = this.updateDrumsetVolumeFrom ?? this.drumset.volume;
    clearTimeout(this.undoUpdateTimer);
    this.drumset.volume = volume;
    this.undoUpdateTimer = window.setTimeout(() => {
      if (volume !== this.updateDrumsetVolumeFrom) {
        this.undoManager.executeCommand(
          new UpdateDrumsetVolumeCommand(
            this.drumset,
            volume,
            this.updateDrumsetVolumeFrom,
          ),
        );
      }
      this.updateDrumsetVolumeFrom = undefined;
    }, UNDO_DELAY);
  }

  @action
  updateInstrumentID(value: string) {
    const id = parseInt(value);
    if (id !== this.instrument.id) {
      const command = new UpdateInstrumentIdCommand(this.drumset, this.instrument, id);
      command.on('execute', (instrument) => {
        this.selectInstrument(instrument);
      });
      command.on('reverse', (instrument) => {
        this.selectInstrument(instrument);
      });
      this.undoManager.executeCommand(command);
    }
  }

  updateVolumeCommands?: UpdateInstrumentVolumeCommand[];
  @action
  updateVolume(value: string) {
    const volume = parseInt(value);
    this.updateVolumeCommands =
      this.updateVolumeCommands ??
      allInstruments(this.instrument).map(
        (instrument) => new UpdateInstrumentVolumeCommand(instrument, volume),
      );
    clearTimeout(this.undoUpdateTimer);
    this.instrument.volume = volume;
    this.updateVolumeCommands.forEach((c) => (c.toVolume = volume));
    this.undoUpdateTimer = window.setTimeout(() => {
      const commands = this.updateVolumeCommands!;
      this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
      this.updateVolumeCommands = undefined;
    }, UNDO_DELAY);
  }

  updatePolyphonyCommands?: UpdateInstrumentPolyphonyCommand[];
  @action
  updatePolyphony(value: string) {
    const poly = parseInt(value);
    this.updatePolyphonyCommands =
      this.updatePolyphonyCommands ??
      allInstruments(this.instrument).map(
        (instrument) => new UpdateInstrumentPolyphonyCommand(instrument, poly),
      );
    clearTimeout(this.undoUpdateTimer);
    this.instrument.poly = poly;
    this.updatePolyphonyCommands.forEach((c) => (c.toPoly = poly));
    this.undoUpdateTimer = window.setTimeout(() => {
      const commands = this.updatePolyphonyCommands!;
      this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
      this.updatePolyphonyCommands = undefined;
    }, UNDO_DELAY);
  }

  updateChokeGroupCommands?: UpdateInstrumentChokeGroupCommand[];
  @action
  updateChokeGroup(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const chokeGroup = parseInt(input.value);
    if (isNaN(chokeGroup)) { input.value = String(this.instrument.chokeGroup); return; }
    this.updateChokeGroupCommands =
      this.updateChokeGroupCommands ??
      allInstruments(this.instrument).map(
        (instrument) => new UpdateInstrumentChokeGroupCommand(instrument, chokeGroup),
      );
    clearTimeout(this.undoUpdateTimer);
    this.instrument.chokeGroup = chokeGroup;
    this.updateChokeGroupCommands.forEach((c) => (c.toChokeGroup = chokeGroup));
    this.undoUpdateTimer = window.setTimeout(() => {
      const commands = this.updateChokeGroupCommands!;
      this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
      this.updateChokeGroupCommands = undefined;
    }, UNDO_DELAY);
  }

  updateFillChokeGroupCommands?: UpdateInstrumentFillChokeGroupCommand[];
  @action
  updateFillChokeGroup(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const fillChokeGroup = parseInt(input.value);
    if (isNaN(fillChokeGroup)) { input.value = String(this.instrument.fillChokeGroup); return; }
    this.updateFillChokeGroupCommands =
      this.updateFillChokeGroupCommands ??
      allInstruments(this.instrument).map(
        (instrument) =>
          new UpdateInstrumentFillChokeGroupCommand(instrument, fillChokeGroup),
      );
    clearTimeout(this.undoUpdateTimer);
    this.instrument.fillChokeGroup = fillChokeGroup;
    this.updateFillChokeGroupCommands.forEach(
      (c) => (c.toFillChokeGroup = fillChokeGroup),
    );
    this.undoUpdateTimer = window.setTimeout(() => {
      const commands = this.updateFillChokeGroupCommands!;
      this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
      this.updateFillChokeGroupCommands = undefined;
    }, UNDO_DELAY);
  }

  @action
  updateFillChokeDelay(value: string) {
    const fillChokeDelay = parseInt(value);
    if (fillChokeDelay !== this.instrument.fillChokeDelay) {
      const commands = allInstruments(this.instrument).map(
        (instrument) =>
          new UpdateInstrumentFillChokeDelayCommand(instrument, fillChokeDelay),
      );
      this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
    }
  }

  @action
  updateNonPercussion(value: number) {
    if (value !== this.instrument.nonPercussion) {
      const commands = allInstruments(this.instrument).map(
        (instrument) => new UpdateInstrumentNonPercussionCommand(instrument, value),
      );
      this.undoManager.executeCommand(new CompositeCommand(commands[0]!.name, commands));
    }
  }

  get typedInstrument(): Instrument {
    return this.instrument as Instrument;
  }

  <template>
    <div class="flex flex-col h-full gap-2 dark:text-white">
      <header class="flex items-start justify-between gap-4 p-2 border-b shrink-0 dark:border-primary-darkBorderColor">
        <div class="flex flex-row gap-2">
        <EditableField
          @model={{@drumset}}
          @field="name"
          @onChange={{@renameDrumset}}
          class="text-2xl font-bold"
        >{{@drumset.name}}</EditableField>
         {{#if (eq @drumset.isDRMX true)}}
            <div class="py-1">
              <img src="/images/editor/hdDrumsetDark.png" alt="HD Drumset" class="h-6 w-6 h-min hidden dark:block"/>
              <img src="/images/editor/hdDrumsetLight.png" alt="HD Drumset" class="h-6 w-6 h-min dark:hidden"/>
            </div>
        {{/if}}
        </div>
        <label class="flex items-center gap-1 text-nowrap shrink-0">
          <div class="w-52">Volume ({{@drumset.volumeDb}}dB)</div>
          <input
            type="range"
            min="1"
            max="159"
            value={{@drumset.volume}}
            class="w-full accent-primary"
            {{on "input" (pick "target.value" this.updateDrumsetVolume)}}
            {{on "dblclick" (fn this.updateDrumsetVolume "100")}}
          />
        </label>
      </header>
      <div class="flex items-start h-full overflow-auto">
        <div
          data-test-instrument-list
          class="flex flex-col h-full overflow-auto w-60 grow-0 shrink-0"
        >
          <nav aria-label="instrumnets menu" class="flex gap-1 px-2">
            <Button
              @icon="plus"
              id="add-instrument"
              {{on "click" this.toggleShowInstrumentList}}
            >Create Instrument</Button>
            {{#if this.showInstrumentList}}
              <div
                class="absolute z-50 flex flex-col overflow-auto bg-white shadow-md max-h-96 overflow-y-auto"
                {{velcro "#add-instrument" middleware=(this.popupMiddleware)}}
                {{onClickOutside (fn this.toggleShowInstrumentList false)}}
              >
                <MenuButton
                  {{on
                    "click"
                    (queue
                      (fn this.addInstrument undefined)
                      (fn this.toggleShowInstrumentList false)
                    )
                  }}
                >Other Instrument</MenuButton>
                {{#each this.availableInstruments as |instrument|}}
                  <MenuButton
                    {{on
                      "click"
                      (queue
                        (fn this.addInstrument instrument)
                        (fn this.toggleShowInstrumentList false)
                      )
                    }}
                  >{{instrument.id}}. {{instrument.name}}</MenuButton>
                {{/each}}
              </div>
            {{/if}}
            <Button
              @icon="arrow-down-to-square"
              id="import-instruments"
              {{on "click" this.toggleShowImportModal}}
            >Import</Button>
            {{#if this.showImportModal}}
              <InstrumentImpport
                @drumset={{this.drumset}}
                @addInstruments={{queue
                  this.addInstruments
                  (fn this.toggleShowImportModal false)
                }}
                class="absolute z-50 flex flex-col min-h-0 p-2 overflow-auto bg-white rounded shadow-md"
                {{velcro
                  "#import-instruments"
                  placement="bottom-start"
                  middleware=(this.popupMiddleware)
                }}
                {{onClickOutside (fn this.toggleShowImportModal false)}}
              />
            {{/if}}
          </nav>
          {{! template-lint-disable no-invalid-interactive }}
          <div
            class="flex flex-col w-full h-full gap-px px-2 py-1 overflow-auto"
            {{onKey "ArrowDown" this.selectNextInstrument}}
            {{onKey "ArrowDown" this.selectNextInstrument shiftKey=true}}
            {{onKey "ArrowUp" this.selectPreviousInstrument}}
            {{onKey "ArrowUp" this.selectPreviousInstrument shiftKey=true}}
            {{onKey "Delete" this.deleteSelectedInstruments}}
            {{onKey "Backspace" this.deleteSelectedInstruments}}
            {{onKey "Escape" (fn this.selectInstrument undefined)}}
            {{on "click" (fn this.selectInstrument undefined)}}
          >
            {{#each this.instruments as |instrument|}}
              <div>
                {{! template-lint-disable no-invalid-interactive }}
                <div
                  class="flex items-center w-full gap-1 overflow-hidden rounded instrument text-nowrap playlist text-ellipsis whitespace-nowrap selected:bg-primary selected:text-white group/options dark:selected:bg-primary-darkButtonSelected dark:selected:text-black"
                  title={{instrument.name}}
                  {{on "click" (fn this.selectInstrument instrument)}}
                  {{selected when=(this.isSelectedInstrument instrument)}}
                >
                  <SelectableItem
                    @model={{instrument}}
                    @field="name"
                  {{!--  @filter={{nameFilter}} --}}
                    @when={{and
                      (this.isSelectedInstrument instrument)
                      (eq 1 this.selectedInstruments.length)
                    }}
                    @onChange={{fn this.renameInstrument instrument}}
                    title={{instrument.name}}
                    class="instrument-name"
                    {{selected when=(this.isSelectedInstrument instrument)}}
                  >{{instrument.id}}. {{instrument.name}}</SelectableItem>
                  <div
                    class="relative invisible text-black group-hover/options:visible selected:visible"
                    {{selected when=(this.isSelectedInstrument instrument)}}
                  >
                    <OptionButton
                      id={{concat "instrument-menu-" instrument.id}}
                      class="text-white group-hover/options:text-black selected:group-hover/options:text-white dark:group-hover/options:text-white dark:selected:group-hover/options:text-black dark:text-black"
                      {{selected when=(this.isSelectedInstrument instrument)}}
                      {{on "click" (fn this.showMenuFor instrument)}}
                    />
                    {{#if (this.shouldShowMenuFor instrument)}}
                      <nav
                        aria-label="Instrument options"
                        class="fixed z-50 flex flex-col bg-white border rounded shadow-lg dark:border-primary-darkBorderColor"
                        {{velcro
                          (htmlId (concat "instrument-menu-" instrument.id))
                          placement="left-start"
                        }}
                        {{onClickOutside (fn this.showMenuFor undefined)}}
                      >
                        <MenuButton
                          @icon="trash-can"
                          title="Delete instrument"
                          {{on
                            "click"
                            (queue
                              (fn this.deleteInstrument instrument)
                              (fn this.showMenuFor undefined)
                            )
                          }}
                        >Delete</MenuButton>
                      </nav>
                    {{/if}}
                  </div>
                </div>
              </div>
            {{/each}}
          </div>
        </div>
        <div
          data-test-instrument-panel
          class="flex flex-col h-full overflow-auto border-l grow dark:border-primary-darkBorderColor"
        >
          {{#if (gte this.selectedInstruments.length 1)}}
            {{#if (eq 1 this.selectedInstruments.length)}}
              <SampleEditor
                @drumset={{this.drumset}}
                @instrument={{this.typedInstrument}}
                class="border-t dark:border-primary-darkBorderColor"
                as |addSamples autoSort|
              >
                <InstrumentSettingsEditor
                  @instrument={{this.instrument}}
                  @renameInstrument={{fn this.renameInstrument this.instrument}}
                  @addSamples={{addSamples}}
                  @autoSort={{autoSort}}
                  @availableMidiIds={{this.availableMidiIds}}
                  @updateInstrumentID={{this.updateInstrumentID}}
                  @updateChokeGroup={{this.updateChokeGroup}}
                  @updateFillChokeGroup={{this.updateFillChokeGroup}}
                  @updateFillChokeDelay={{this.updateFillChokeDelay}}
                  @updateNonPercussion={{this.updateNonPercussion}}
                  @updatePolyphony={{this.updatePolyphony}}
                  @updateVolume={{this.updateVolume}}
                />
              </SampleEditor>
            {{else}}
              <InstrumentSettingsEditor
                @renameInstrument={{fn this.renameInstrument this.instrument}}
                @instrument={{this.instrument}}
                @availableMidiIds={{this.availableMidiIds}}
                @updateInstrumentID={{this.updateInstrumentID}}
                @updateChokeGroup={{this.updateChokeGroup}}
                @updateFillChokeGroup={{this.updateFillChokeGroup}}
                @updateFillChokeDelay={{this.updateFillChokeDelay}}
                @updateNonPercussion={{this.updateNonPercussion}}
                @updatePolyphony={{this.updatePolyphony}}
                @updateVolume={{this.updateVolume}}
              />
            {{/if}}
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    DrumsetEditor: typeof DrumsetEditorComponent;
  }
}
